<template>
  <div class="page page-full" :class="{'page-loading': loading}">
    <div class="page-header with-actions">
      <div class="d-flex w-50">
        <h1 class="d-flex align-items-center mr-3">Team Members</h1>
        <v-form-group class="w-50 client-dropdown" label="Client" v-if="isClientAdmin">
          <v-select :class="[{ 'vs--has-value': selectedClient !== null }]"
                    :options="clientOptions"
                    :clearable="false"
                    :filterable="false"
                    :searchable="false"
                    v-model="selectedClient"
                    @input="onClientSelect">
          </v-select>
        </v-form-group>
      </div>
      <div class="actions">
        <b-btn @click="createEmployee" variant="success">Add Team Member</b-btn>
      </div>
    </div>
    <page-loading v-if="loading" />
    <div v-else class="row team-members">
      <div class="col-12" v-if="employees.length == 0 && !isClientAdmin">
        You have no Team Members yet. Add someone…it gets lonely in here!
      </div>
      <div class="col-12" v-if="employees.length == 0 && isClientAdmin">
        No Team Members to display.
      </div>
      <div class="col-12 col-xl-4 col-lg-6 col-md-6 mb-4" v-for="employee in employees" :key="employee.id">
        <b-card class="team-member">
          <h4 class="team-member-title">{{employee.firstName}} {{employee.lastName}}</h4>
          <div class="team-member-email">{{employee.email}}</div>
          <div class="team-member-permissions">
            <span>Permissions</span>
            <v-select :value="getRoleValue(employee.roleId)"
                      :options="rolesList"
                      :clearable="false"
                      :filterable="false"
                      :searchable="false"
                      :disabled="roleLoading"
                      @input="(value) => changeRole(employee.id, value)" />
          </div>
          <div class="team-member-actions">
            <div v-if="employee.status == userStatus.Pending">
              <b-btn class="team-member-resend" variant="link" @click="resendInvite(employee.id)">
                <svg-icon name="pending" /> Resend Invite
              </b-btn>
            </div>
            <div v-if="employee.status == userStatus.Active">
              <div class="team-member-status">
                <svg-icon name="active" /> Active
              </div>
            </div>
            <div v-if="employee.status == userStatus.Disabled">
              <div class="team-member-status text-danger">
                <svg-icon name="disabled" /> Disabled
              </div>
            </div>
            <div v-if="employee.status != userStatus.Disabled">
              <b-btn class="team-member-delete" variant="link" @click="deleteEmployee(employee.id)">
                <svg-icon name="delete" /> Delete
              </b-btn>
            </div>
          </div>
        </b-card>
      </div>
    </div>
  </div>
</template>

<script>
  import Loading from "components/common/loading";
  import employeeService from "services/employee-service";
  import adminService from "services/admin-service";
  import role from "enums/role";
  import userStatus from "enums/user-status";

  export default {
    name: 'EmployeesList',
    data() {
      return {
        loading: false,
        roleLoading: false,
        employees: [],
        clientOptions: [],
        selectedClient: null,
        role,
        userStatus,
        rolesList: [
          { label: 'Client Admin', value: role.Client },
          { label: 'Full User', value: role.ClientUser },
          { label: 'Only Order', value: role.ClientOrderer },
          { label: 'Provider', value: role.ClientProvider }
        ]
      }
    },
    components: {
      'page-loading': Loading
    },
    async mounted() {
      if (!this.isClientAdmin) {
        await this.getEmployees();
      }
      else {
        this.clientOptions = (await adminService.getClientsForClientAdmin()).map(c => {
          return {
            label: c.name,
            value: c.id
          }
        }).sort((a, b) => a.label.localeCompare(b.label));

        let client = this.clientOptions.find(c => c.value.toString() === this.$route.query.clientId);
        
        if (!client)
          return;

        this.selectedClient = client;
        await this.getEmployees();
      }
    },
    computed: {
      isClientAdmin() {
        return this.$auth.isInRole("ClientAdmin");
      }
    },
    methods: {
      getRoleValue(roleId) {
        let role = this.rolesList.find(role => role.value == roleId);
        return role?.label || '';
      },

      async getEmployees() {
        this.loading = true;
        this.employees = await employeeService.getAll(this.selectedClient?.value);
        this.loading = false;
      },

      createEmployee() {
        if (this.isClientAdmin) {
          if (this.selectedClient === null) {
            this.$root.errorToast("Select a client!");

            return;
          }

          this.$router.push({ path: "employees/create", query: { clientId: this.selectedClient.value } });

          return;
        }

        this.$router.push("employees/create");
      },

      async resendInvite(id) {
        await employeeService.resendInvite(id);
        this.$root.successToast("Invite sent!");
        await this.getEmployees();
      },

      async deleteEmployee(id) {
        await employeeService.delete(id);
        this.$root.successToast("Team member deleted!");
        await this.getEmployees();
      },

      async changeRole(id, { value: newRole }) {
        this.roleLoading = true;
        let employee = this.employees.find(employee => employee.id == id);
        
        if (employee)
          employee.roleId = newRole;

        await employeeService.changeRole(id, newRole);
        this.$root.successToast("Role changed!");
        this.roleLoading = false;
        await this.getEmployees();
      },
      async onClientSelect() {
        await this.getEmployees();
      }
    }
  };
</script>
<style lang="scss">
  @import "@/styles/_variables";

  .client-dropdown {
    .v-select {
      min-width: 200px;
      max-width: 300px;
    }
  }

  .team-members {
    .team-member {
      padding: 0;

      .card-body {
        padding: 2.5rem;

        .team-member-title {
          font-size: 1.25rem;
          padding: 0;
          margin: 0;
          font-weight: 400;
          margin-bottom: .5rem;
        }

        .team-member-email {
          font-size: .875rem;
          font-weight: 500;
          margin-bottom: .75rem;
        }

        .team-member-permissions {
          font-size: .875rem;
          font-weight: 500;
          display: flex;
          align-items: center;

          .v-select {
            width: auto;
            margin-left: 1rem;

            .vs__dropdown-toggle {
              border: none;
              box-shadow: none;
              outline: none;
              font-size: 1rem;
              line-height: 1;
              height: auto;
              color: $text-color;
              padding: 0;

              .vs__actions {
                padding: 0 0 0 .5rem;

                .vs__open-indicator {
                  svg {
                    color: $text-color;
                    width: .875rem;
                    height: auto;
                  }
                }
              }
            }

            .vs__dropdown-menu {
              border: .0625rem solid $border-color;
            }

            &.vs--single {
              &.vs--open {
                .vs__selected {
                  position: static;
                }
              }
            }

            &.vs--disabled {
              .vs__dropdown-toggle {
                background: transparent;

                .vs__selected-options {
                  .vs__selected {
                    color: $disabled-color;
                  }
                }
                
                .vs__actions {
                  .vs__open-indicator {
                    background: transparent;

                    svg {
                      color: $disabled-color;
                    }
                  }
                }
              }
            }
          }
        }

        .team-member-actions {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-top: 1.75rem;

          .team-member-resend,
          .team-member-delete {
            padding: 0;
            outline: none;
            box-shadow: none;
            border: none;
            text-decoration: none;
            color: $body-color;
            transition: .2s all ease-in;
            font-size: 1rem;

            .svg-icon {
              height: 1.125em;
              width: auto;
            }
          }

          .team-member-resend {
            color: $warning;
            font-weight: $font-weight-bold;

            @include hover-focus {
              color: darken($warning, 5%);
            }
          }

          .team-member-delete {
            font-weight: 500;

            @include hover-focus {
              color: $danger;
            }
          }

          .team-member-status {
            font-size: 1rem;
            font-weight: 500;

            .svg-icon {
              height: 1.125em;
              width: auto;
              margin-top: -0.125em;
              margin-right: 0.5em;
            }
          }
        }
      }
    }
  }
</style>